import { turbine } from "neql"
import React, { useCallback, useRef } from "react"
import { Text, Loader, IconEdit } from "dash-styled"
import { ReactComponent as IconDrag } from "icons/lib/drag.svg"
import { ReactComponent as IconTrash } from "icons/lib/trash.svg"
import { useDrag, useDrop, DropTargetMonitor, XYCoord } from "react-dnd"
import { StructuredSequence } from "../../../../../../../../../Hooks/useStructuredTrainingQuestionTutorial"

const SEQUENCE_ITEM = "sequence_item"

const contentTypeMapping = (
    type: turbine.TrainingQuestionTutorialType
): string => {
    switch (type.valueOf()) {
        case turbine.TrainingQuestionTutorialType.FreeMode:
            return "Explanation"
        case turbine.TrainingQuestionTutorialType.FromBaseline:
        case turbine.TrainingQuestionTutorialType.FromJob:
            return "Example"
        default:
            return ""
    }
}

interface TutorialContentTableRowProps {
    index: number
    sequence: StructuredSequence
    onMoveItem: (dragIndex: number, hoverIndex: number) => void
    onEndMove: (id: string) => void
    onDelete: (id: string) => void
    onEdit: (item: StructuredSequence) => void
}

interface DragItem {
    index: number
    id: string
    type: string
}

export const TutorialContentTableRow = ({
    index,
    sequence,
    onMoveItem,
    onEndMove,
    onDelete,
    onEdit
}: TutorialContentTableRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null)
    const { id, step, type, summary, deletionInProgress } = sequence

    const handleDelete = useCallback(() => id && onDelete(id), [id, onDelete])
    const handleEdit = useCallback(() => onEdit(sequence), [onEdit, sequence])
    const [, drop] = useDrop({
        accept: SEQUENCE_ITEM,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId()
            }
        },
        hover(item: DragItem, monitor: DropTargetMonitor) {
            if (!ref.current) {
                return
            }

            if (item.id === undefined || id === undefined) {
                return
            }

            const dragIndex = item.index
            const hoverIndex = index

            if (dragIndex === hoverIndex) {
                return
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect()
            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
            const clientOffset = monitor.getClientOffset()
            const hoverClientY =
                (clientOffset as XYCoord).y - hoverBoundingRect.top

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return
            }

            onMoveItem(dragIndex, hoverIndex)
            item.index = hoverIndex
        }
    })

    const [{ isDragging }, drag] = useDrag({
        type: SEQUENCE_ITEM,
        item: () => ({ id, index }),
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging()
        }),
        end: item => {
            if (item.id !== undefined) {
                onEndMove(item.id)
            }
        }
    })

    const opacity = isDragging ? "opacity-0" : "opacity-1"
    drag(drop(ref))

    const cellClassName =
        "p-2 border-0 border-b border-solid border-rhino-300 h-full"
    return (
        <>
            {id !== undefined && type !== undefined && type !== null && (
                <tr className={`text-sm ${opacity}`} ref={ref}>
                    <td className={`${cellClassName}`}>
                        <IconDrag className="cursor-move" />
                    </td>
                    <td className={`${cellClassName} text-smoke-610 my-0`}>
                        {step}
                    </td>
                    <td
                        className={`${cellClassName} text-smoke-610 my-0 truncate`}
                    >
                        {contentTypeMapping(type)}
                    </td>
                    {type.valueOf() ===
                    turbine.TrainingQuestionTutorialType.FreeMode ? (
                        <td
                            className={`${cellClassName} text-smoke-610 my-0 font-semibold`}
                        >
                            {summary.hint && (
                                <Text
                                    className="text-smoke-610 my-0 truncate"
                                    size="sm"
                                >
                                    Hint: {summary.hint}
                                </Text>
                            )}
                            {summary.explanation && (
                                <Text
                                    className="text-smoke-610 my-0 truncate"
                                    size="sm"
                                >
                                    Explanation: {summary.explanation}
                                </Text>
                            )}
                        </td>
                    ) : (
                        <td
                            className={`${cellClassName} text-grape-800 my-0 truncate font-semibold`}
                        >
                            {summary.jobId}

                            {summary.hint && (
                                <Text
                                    className="text-smoke-610 my-0 truncate"
                                    size="sm"
                                >
                                    Hint: {summary.hint}
                                </Text>
                            )}
                            {summary.explanation && (
                                <Text
                                    className="text-smoke-610 my-0 truncate"
                                    size="sm"
                                >
                                    Explanation: {summary.explanation}
                                </Text>
                            )}
                        </td>
                    )}
                    <td
                        className={`${cellClassName} flex justify-start items-center`}
                    >
                        <button
                            onClick={handleEdit}
                            className="text-smoke-800 mr-4"
                        >
                            <IconEdit className="w-4 h-4" />
                        </button>
                        {deletionInProgress ? (
                            <Loader className="justify-end" size="xxxl" />
                        ) : (
                            <IconTrash
                                className="cursor-pointer text-smoke-800 w-4 h-4"
                                onClick={handleDelete}
                            />
                        )}
                    </td>
                </tr>
            )}
        </>
    )
}
